/**
 * Financial Info Mock Data Panel
 *
 * 开发环境下的快捷填充工具
 * 提供可视化的面板用于快速填充财务信息数据
 *
 * @module mockDataGenerator.demo
 */
import { Button } from "antd";
import { FormInstance } from "antd/lib/form";
import { useState } from "react";
import {
  financialMockDataPresets,
  generateFinancialInfoMockData,
} from "./mockDataGenerator.financial-info";
import { delay } from "@/utils";

interface FinancialInfoMockDataPanelProps {
  /** 表单实例 */
  form?: FormInstance;
}

/**
 * 财务信息 Mock 数据生成工具面板
 */
export function FinancialInfoMockDataPanel({
  form,
}: FinancialInfoMockDataPanelProps) {
  const [visible, setVisible] = useState(false);

  if (!form) {
    return null;
  }

  /* ============================================================
   * 预设场景操作
   * ========================================================== */

  const fillFormData = async (data: any, logMessage: string) => {
    // 多步骤确保条件字段正确显示和填充
    console.log(
      "🔧 开始填充数据，isUSPerson:",
      data.isUSPerson,
      "tins:",
      data.tins
    );

    // Step 1: 先清空相关字段
    form.resetFields(["isUSPerson", "tins"]);

    // Step 2: 使用模拟点击的方式来触发isUSPerson变化
    // 这样会触发onChange事件，让DynamicForm重新计算visible
    await delay(200);
    // 找到"否"单选框并模拟点击
    const noRadio = document.querySelector(
      'input[type="radio"][value="NO"]'
    ) as HTMLInputElement;
    if (noRadio && !noRadio.checked) {
      console.log('🔧 模拟点击"否"单选框');
      noRadio.click();
    }

    // 等待DOM更新后设置所有数据
    await delay(200);
    console.log("🔧 设置完整数据");
    form.setFieldsValue(data);
    console.log(logMessage, data);

    // 最终检查
    const currentValues = form.getFieldsValue();
    console.log("🔍 当前表单值:", currentValues);
  };

  const handleFillFullTimeEmployed = () => {
    const data = financialMockDataPresets.fullTimeEmployedTech();
    fillFormData(data, "✅ 已填充全职技术工作者:");
  };

  const handleFillSelfEmployed = () => {
    const data = financialMockDataPresets.selfEmployed();
    fillFormData(data, "✅ 已填充自雇商业人士:");
  };

  const handleFillHighNetWorth = () => {
    const data = financialMockDataPresets.highNetWorth();
    fillFormData(data, "✅ 已填充高净值投资者:");
  };

  const handleFillStudent = () => {
    const data = financialMockDataPresets.student();
    fillFormData(data, "✅ 已填充学生:");
  };

  const handleFillRetired = () => {
    const data = financialMockDataPresets.retired();
    fillFormData(data, "✅ 已填充退休人士:");
  };

  const handleFillRandom = () => {
    const data = generateFinancialInfoMockData();
    fillFormData(data, "✅ 已填充随机财务数据:");
  };

  const handleClear = () => {
    form.resetFields();
    console.log("✅ 已清空财务信息表单");
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            💼
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "320px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              💼 财务信息 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "500px" }}
          >
            {/* 预设场景 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                预设场景
              </div>
              <Button size="small" block onClick={handleFillFullTimeEmployed}>
                全职技术工作者
              </Button>
              <Button size="small" block onClick={handleFillSelfEmployed}>
                自雇商业人士
              </Button>
              <Button size="small" block onClick={handleFillHighNetWorth}>
                高净值投资者
              </Button>
              <Button size="small" block onClick={handleFillStudent}>
                学生
              </Button>
              <Button size="small" block onClick={handleFillRetired}>
                退休人士
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 其他操作 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                其他操作
              </div>
              <Button size="small" block onClick={handleFillRandom}>
                随机生成
              </Button>
            </div>
          </div>

          {/* 清空按钮 */}
          <div className="px-4 pb-4 pt-2 border-t">
            <Button danger size="small" block onClick={handleClear}>
              清空表单
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default FinancialInfoMockDataPanel;
